import type {
  EndpointBuilder,
  BaseQueryFn as RTKBaseQueryFn,
} from '@reduxjs/toolkit/query'
import { createApi } from '@reduxjs/toolkit/query/react'
import type { AxiosError, AxiosRequestConfig } from 'axios'

import httpService from '@/utils/httpService'

// Define interfaces for API responses and requests
export interface ErrorResponse {
  status?: number
  data?: unknown
  message?: string
}

export interface Subscription {
  id: string
  plan: {
    id: string
    interval: string
    name: string
  }
  stripe_subscription?: string
}

export interface ProfileResponse {
  user: string
  name?: string
  country?: string
  address1?: string
  city?: string
  postal_code?: string
  serial_number?: string
  source?: string
  vat?: string
  subscriptions?: Subscription[]
  language?: string
  signup_state?: string
}

interface ImagePresignedUrlParams {
  fileName: string
  imageType?: 'images'
  keyLatestImage?: string
}

interface CaseCreateParams {
  id: string
  name: string
}

interface CaseDetailsResponse {
  id: string
  name: string
  case_type?: string
  status?: string
  role?: string
  description?: string
  dentist?: {
    id: string
    name: string
  }
  images?: Array<{
    id: string
    image: string
    key_raw: string
    calibrated_image?: boolean
    type?: string
    rawWidth?: number
    rawHeight?: number
    keyLatestImage?: string | null
    note?: string | null
    raw_image?: string
    recipe_data?: Record<string, unknown>
  }>
  [key: string]: any
}

interface CaseImageUploadParams {
  caseId: string
  key: string
}

interface ImageUpdateParams {
  imageId: number
  key: string
  note: string
}

interface DentistCreateParams {
  name: string
  user: number
}

interface DentistUpdateParams {
  caseId: string
  name: string
  patient_name?: string
  dentist: number
}

interface ProjectUpdateParams {
  caseId: string
  name: string
}

interface ProjectUpdateParams {
  caseId: string
  name: string
  case_type?: string
}
interface DehazeImageFilter {
  id: string
  haze_mode: any
  brightness: any
  invert: boolean
}
interface RecipeImagesUpdate {
  id: string
  dehazingResetedValue: boolean
}

// Type for the base query return
type BaseQueryFn = RTKBaseQueryFn<AxiosRequestConfig, unknown, ErrorResponse>

// Axios base query with Auth0 integration
const axiosBaseQuery: BaseQueryFn = async (arg) => {
  try {
    const result = await httpService({
      ...arg,
    })

    return { data: result.data }
  } catch (error) {
    const axiosError = error as AxiosError
    return {
      error: {
        status: axiosError.response?.status,
        data: axiosError.response?.data || axiosError.message,
      } as ErrorResponse,
    }
  }
}

type TagTypes = 'Profile' | 'Case' | 'Image' | 'Metadata' | 'Recipes'

// Update the Builder type to include TagTypes
type Builder = EndpointBuilder<BaseQueryFn, TagTypes, 'api'>

// Query endpoints
const queries = (builder: Builder) => ({
  getProfile: builder.query<ProfileResponse, void>({
    query: () => ({
      url: '/users/profile/',
      method: 'GET',
    }),
    providesTags: [{ type: 'Profile' }],
  }),

  getMetadata: builder.query<unknown, void>({
    query: () => ({
      url: '/masters/core/meta',
      method: 'GET',
    }),
    providesTags: [{ type: 'Metadata' }],
  }),

  getImagePresignedUrl: builder.query<unknown, string>({
    query: (fileName) => ({
      url: `/cases/images/presigned/?file=${encodeURIComponent(fileName)}`,
      method: 'GET',
    }),
  }),
  getCaseDetails: builder.query<
    CaseDetailsResponse,
    { caseId: string; ordering?: string }
  >({
    query: ({ caseId, ordering }) => {
      return {
        url: `/cases/${caseId}/`,
        method: 'GET',
        params: ordering ? { ordering } : {},
      }
    },
    providesTags: [{ type: 'Case' }],
  }),

  editImage: builder.query<unknown, ImagePresignedUrlParams>({
    query: ({ fileName, keyLatestImage }) => ({
      url: `/cases/images/presigned/?file=${fileName}&image_type=images&key=${keyLatestImage}`,
      method: 'GET',
    }),
  }),

  withouteditImage: builder.query<unknown, string>({
    query: (fileName) => ({
      url: `/cases/images/presigned/?file=${fileName}&image_type=images`,
      method: 'GET',
    }),
  }),

  clearSession: builder.query<unknown, any>({
    query: (type) => ({
      url: `/users/session/clear_session/`,
      method: 'GET',
      params: { type },
    }),
  }),

  getDentist: builder.query<unknown, any>({
    query: () => ({
      url: `/masters/dentists`,
      method: 'GET',
    }),
  }),

  getCountry: builder.query<any, any>({
    query: () => ({
      url: `/masters/countries/`,
      method: 'GET',
    }),
  }),

  getPlans: builder.query<unknown, any>({
    query: () => ({
      url: `/subscriptions/plans/`,
      method: 'GET',
    }),
  }),

  getStripePlans: builder.query<unknown, any>({
    query: () => ({
      url: `/users/stripe/portal/`,
      method: 'GET',
    }),
  }),
  getRecipes: builder.query<
    unknown,
    {
      shared?: boolean
      limit?: number
      offset?: number
      status?: any
      deleted_cases?: boolean
      ordering?: string
    }
  >({
    query: (params) => ({
      url: '/cases/',
      method: 'GET',
      params,
    }),
    providesTags: (result) => (result ? [{ type: 'Recipes', id: 'LIST' }] : []),
  }),

  getInvitedUser: builder.query<unknown, any>({
    query: (id) => ({
      url: `/cases/${id}/invite/`,
      method: 'GET',
    }),
  }),

  getSharedAccessUser: builder.query<unknown, any>({
    query: (id) => ({
      url: `/cases/${id}/shared/`,
      method: 'GET',
    }),
  }),

  getRecipeDetail: builder.query<unknown, any>({
    query: ({
      caseId,
      limit,
      offset,
      search,
      ordering,
      paginated,
      recipe_type,
    }) => ({
      url: `/cases/${caseId}/recipes/`,
      method: 'GET',
      params: {
        limit,
        offset,
        search,
        ordering,
        paginated,
        recipe_type,
      },
    }),
  }),
})

// Mutation endpoints
const mutations = (builder: Builder) => ({
  createCase: builder.mutation<unknown, CaseCreateParams>({
    query: (data) => ({
      url: '/cases/',
      method: 'POST',
      headers: {
        case: data.id,
      },
      data,
    }),
  }),

  uploadCaseImages: builder.mutation<unknown, CaseImageUploadParams>({
    query: ({ caseId, key }) => ({
      url: `/cases/${caseId}/image-uploaded/`,
      method: 'POST',
      data: { key },
    }),
  }),

  updateRecipeImage: builder.mutation<unknown, ImageUpdateParams>({
    query: ({ imageId, key, note }) => {
      const data: Record<string, any> = {}
      if (key !== undefined) data.key = key
      if (note !== undefined) data.note = note

      return {
        url: `/cases/images/${imageId}/update/`,
        method: 'POST',
        data,
      }
    },
  }),
  deleteImage: builder.mutation<
    unknown,
    { imageId: string | number; caseId: string }
  >({
    query: ({ imageId }) => ({
      url: `/cases/images/${imageId}/`,
      method: 'DELETE',
    }),
    async onQueryStarted({ caseId }, { dispatch, queryFulfilled }) {
      try {
        await queryFulfilled
        dispatch(
          apiService.endpoints.getCaseDetails.initiate(
            { caseId },
            {
              forceRefetch: true,
            },
          ),
        )
      } catch (error) {
        console.error('Error in deleteImage mutation:', error)
      }
    },
  }),

  createDentist: builder.mutation<unknown, DentistCreateParams>({
    query: ({ name, user }) => ({
      url: '/masters/dentists/',
      method: 'POST',
      data: { name, user },
    }),
  }),

  updateDentistName: builder.mutation<unknown, DentistUpdateParams>({
    query: ({ caseId, name, dentist }) => ({
      url: `/cases/${caseId}/`,
      method: 'PUT',
      data: { name, dentist },
    }),
  }),

  updateProjectName: builder.mutation<unknown, ProjectUpdateParams>({
    query: ({ caseId, name, case_type }) => ({
      url: `/cases/${caseId}/`,
      method: 'PATCH',
      data: { name, case_type },
    }),
  }),

  dehazeImageFilter: builder.mutation<unknown, DehazeImageFilter>({
    query: ({ id, haze_mode, brightness, invert }) => ({
      url: `/cases/images/${id}/dehaze/`,
      method: 'POST',
      data: { haze_mode, brightness, invert },
    }),
  }),

  recipeImagesUpdate: builder.mutation<unknown, RecipeImagesUpdate>({
    query: ({ id, dehazingResetedValue }) => ({
      url: `/cases/images/${id}/update/`,
      method: 'POST',
      data: {
        recipe_data: {
          dehazingReseted: dehazingResetedValue,
        },
      },
    }),
  }),
  calibrateImage: builder.mutation<unknown, any>({
    query: ({ id, L, x, y }) => ({
      url: `/cases/images/${id}/calibrate/`,
      method: 'POST',
      data: { L, x, y },
    }),
  }),
  generateCrownRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `/recipes/staining-fullmonolithic/`,
      method: 'POST',
      data,
    }),
  }),
  generateShadeGuideMonolithicRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `/recipes/analog-monolithic-recipe/`,
      method: 'POST',
      data,
    }),
  }),
  generateShadeGuideLayeringRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `/recipes/analog-layering-recipe/`,
      method: 'POST',
      data,
    }),
  }),
  generateAllInOneRecipe: builder.mutation({
    query: (data) => ({
      url: `/recipes/generate/`,
      method: 'POST',
      data: data,
    }),
  }),
  generateGingivaStainingRecipe: builder.mutation({
    query: (data) => ({
      url: `/recipes/staining-gingiva/`,
      method: 'POST',
      data: data,
    }),
  }),
  generateStumpShadeRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `/recipes/staining-color-model/`,
      method: 'POST',
      data,
    }),
  }),
  generateWashBakeRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `/recipes/staining-layering/`,
      method: 'POST',
      data,
    }),
  }),
  generateFrameworkRecipe: builder.mutation<
    unknown,
    { url: string; body: any }
  >({
    query: ({ url, body }) => ({
      url: `/recipes/${url}/`,
      method: 'POST',
      data: body,
    }),
  }),

  generateWorkFlowStumpShadeRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `/recipes/aesthetic-model/`,
      method: 'POST',
      data,
    }),
  }),
  generateDentinRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `/recipes/dentin/`,
      method: 'POST',
      data,
    }),
  }),
  generateInternalEffectRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `recipes/internal-effect/`,
      method: 'POST',
      data,
    }),
  }),
  generateEnamelRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `recipes/enamel/`,
      method: 'POST',
      data,
    }),
  }),
  generateGingivaCeramicRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `recipes/gingiva/`,
      method: 'POST',
      data,
    }),
  }),
  generateOpaquerRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `recipes/opaquer/`,
      method: 'POST',
      data,
    }),
  }),
  createProfile: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `users/profile/`,
      method: 'POST',
      data,
    }),
  }),
  planCheckout: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `users/stripe/checkout/`,
      method: 'POST',
      data,
    }),
  }),
  planUpgrade: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `users/stripe/subscription_upgrade/`,
      method: 'POST',
      data,
    }),
  }),
  createRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `cases/`,
      method: 'POST',
      data,
    }),
  }),
  duplicateCase: builder.mutation<unknown, { ids: string[] }>({
    query: ({ ids }) => {
      const params = new URLSearchParams()
      ids.forEach((id) => params.append('ids', id))

      return {
        url: `/cases/duplicate/?${params.toString()}`,
        method: 'POST',
      }
    },
  }),
  restoreImage: builder.mutation<unknown, { ids: string[] }>({
    query: ({ ids }) => {
      const params = new URLSearchParams()
      ids.forEach((id) => params.append('ids', id))

      return {
        url: `/cases/restore-multiple-case/?${params.toString()}`,
        method: 'POST',
      }
    },
    invalidatesTags: [{ type: 'Recipes', id: 'LIST' }],
  }),

  inviteUser: builder.mutation<unknown, any>({
    query: ({ id, data }) => ({
      url: `cases/${id}/invite/`,
      method: 'POST',
      data,
    }),
  }),

  duplicateCaseImage: builder.mutation<
    unknown,
    { caseId: string; ids: string[] }
  >({
    query: ({ caseId, ids }) => {
      const params = new URLSearchParams()
      ids.forEach((id) => params.append('ids', id))

      return {
        url: `/cases/${caseId}/images/duplicate/?${params.toString()}`,
        method: 'POST',
      }
    },
  }),
  moveCaseImage: builder.mutation<
    unknown,
    { caseId: string; ids: string[]; data: any }
  >({
    query: ({ caseId, ids, data }) => {
      const params = new URLSearchParams()
      ids.forEach((id) => params.append('ids', id))

      return {
        url: `/cases/${caseId}/images/move/?${params.toString()}`,
        method: 'POST',
        data,
      }
    },
  }),

  saveRecipe: builder.mutation<unknown, any>({
    query: ({ id, data }) => ({
      url: `/cases/${id}/recipes/`,
      method: 'POST',
      data,
    }),
  }),

  savePdf: builder.mutation<Blob, { id: string; data: any }>({
    queryFn: async ({ id, data }) => {
      try {
        const response = await httpService({
          url: `/cases/${id}/recipes/pdf/`,
          method: 'POST',
          data,
          headers: {
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        })
        return { data: response.data }
      } catch (error) {
        const axiosError = error as AxiosError
        return {
          error: {
            status: axiosError.response?.status,
            data: axiosError.response?.data || axiosError.message,
          },
        }
      }
    },
  }),

  generateDentinEnamelRecipe: builder.mutation<unknown, any>({
    query: (data) => ({
      url: `/recipes/matisse-lite/`,
      method: 'POST',
      data,
    }),
  }),

  // Delete
  deleteCase: builder.mutation<
    unknown,
    { ids: string[]; hard_delete?: boolean }
  >({
    query: ({ ids, hard_delete = false }) => {
      const params = new URLSearchParams()
      ids.forEach((id) => params.append('ids', id))
      params.append('hard_delete', hard_delete ? 'true' : 'false')

      return {
        url: `/cases/delete-multiple-case/?${params.toString()}`,
        method: 'DELETE',
      }
    },
    invalidatesTags: [{ type: 'Recipes', id: 'LIST' }],
  }),

  deleteUserAccess: builder.mutation<unknown, any>({
    query: ({ id, shared_id }) => ({
      url: `/cases/${id}/shared/${shared_id}`,
      method: 'DELETE',
    }),
  }),

  deleteCaseImage: builder.mutation<
    unknown,
    { ids: string[]; caseId?: string }
  >({
    query: ({ ids, caseId }) => {
      const params = new URLSearchParams()
      ids.forEach((id) => params.append('ids', id))

      return {
        url: `/cases/${caseId}/delete-multiple-image/?${params.toString()}`,
        method: 'DELETE',
      }
    },
  }),

  deleteSavedRecipe: builder.mutation<
    unknown,
    { ids: string[]; caseId?: string }
  >({
    query: ({ ids, caseId }) => {
      const params = new URLSearchParams()
      ids.forEach((id) => params.append('ids', id))

      return {
        url: `/cases/${caseId}/recipes/delete-multiple-recipes/?${params.toString()}`,
        method: 'DELETE',
      }
    },
  }),

  removeSharedCase: builder.mutation<unknown, any>({
    query: ({ id }) => ({
      url: `/cases/${id}/`,
      method: 'DELETE',
    }),
  }),

  //Patch
  updateCaseDetail: builder.mutation<unknown, ProjectUpdateParams>({
    query: ({ caseId, ...data }) => ({
      url: `/cases/${caseId}/`,
      method: 'PUT',
      data,
    }),
  }),

  updateRecipeName: builder.mutation<unknown, any>({
    query: ({ caseId, id, ...data }) => ({
      url: `/cases/${caseId}/recipes/${id}/`,
      method: 'PATCH',
      data,
    }),
  }),

  updateAccess: builder.mutation<unknown, any>({
    query: ({ caseId, share_id, ...data }) => ({
      url: `/cases/${caseId}/shared/${share_id}/`,
      method: 'PATCH',
      data,
    }),
  }),
})

// Create the API service
export const apiService = createApi({
  reducerPath: 'api',
  baseQuery: axiosBaseQuery,
  tagTypes: ['Profile', 'Case', 'Image', 'Metadata', 'Recipes'],
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    ...queries(builder as Builder),
    ...mutations(builder as Builder),
  }),
})

// Export hooks for queries
export const {
  useGetMetadataQuery,
  // useEditImageQuery,
  // useWithouteditImageQuery,
  useClearSessionQuery,
  useGetCaseDetailsQuery,
  useGetProfileQuery,
  useGetDentistQuery,
  useGetCountryQuery,
  useGetPlansQuery,
  useGetRecipesQuery,
  useGetInvitedUserQuery,
  useGetSharedAccessUserQuery,
  useGetRecipeDetailQuery,
  // Lazy Queries
  useLazyClearSessionQuery,
  useLazyGetCaseDetailsQuery,
  useLazyGetProfileQuery,
  useLazyGetMetadataQuery,
  useLazyGetImagePresignedUrlQuery,
  useLazyEditImageQuery,
  useLazyWithouteditImageQuery,
  useLazyGetStripePlansQuery,
  useLazyGetRecipesQuery,
  useLazyGetInvitedUserQuery,
  useLazyGetSharedAccessUserQuery,
  useLazyGetRecipeDetailQuery,
} = apiService

// Export hooks for mutations
export const {
  useUpdateRecipeImageMutation,
  useCreateCaseMutation,
  useUploadCaseImagesMutation,
  useDeleteImageMutation,
  useUpdateDentistNameMutation,
  useCreateDentistMutation,
  useUpdateProjectNameMutation,
  useDehazeImageFilterMutation,
  useRecipeImagesUpdateMutation,
  useCalibrateImageMutation,
  useGenerateCrownRecipeMutation,
  useGenerateShadeGuideMonolithicRecipeMutation,
  useGenerateShadeGuideLayeringRecipeMutation,
  useGenerateAllInOneRecipeMutation,
  useGenerateGingivaStainingRecipeMutation,
  useGenerateStumpShadeRecipeMutation,
  useGenerateWashBakeRecipeMutation,
  useGenerateFrameworkRecipeMutation,
  useGenerateWorkFlowStumpShadeRecipeMutation,
  useGenerateDentinRecipeMutation,
  useGenerateInternalEffectRecipeMutation,
  useGenerateEnamelRecipeMutation,
  useGenerateGingivaCeramicRecipeMutation,
  useGenerateOpaquerRecipeMutation,
  useCreateProfileMutation,
  usePlanCheckoutMutation,
  usePlanUpgradeMutation,
  useCreateRecipeMutation,
  useDeleteCaseMutation,
  useUpdateCaseDetailMutation,
  useDuplicateCaseMutation,
  useRestoreImageMutation,
  useInviteUserMutation,
  useDeleteUserAccessMutation,
  useDuplicateCaseImageMutation,
  useDeleteCaseImageMutation,
  useMoveCaseImageMutation,
  useDeleteSavedRecipeMutation,
  useUpdateRecipeNameMutation,
  useSaveRecipeMutation,
  useUpdateAccessMutation,
  useSavePdfMutation,
  useRemoveSharedCaseMutation,
  useGenerateDentinEnamelRecipeMutation,
} = apiService
